/**
 * Assistant Blocks arrangement recipe.
 *
 * The assistant describes regions, not positions: a CSS `grid-template-areas`
 * template for a group, and a rule per area saying which of that group's children
 * belong in it and how they flow. `compileArrangement` turns it into cells against
 * the live graph on every build, so components added later land in their region.
 *
 * `typia.json.schema` derives the LLM-facing schema from these types and their
 * JSDoc — the comments are sent to the model.
 */
import { isRecord } from '@ardoq/common-helpers';
export type RegionFlow = 'row' | 'column' | 'grid' | 'tree';
export type RegionAlign = 'start' | 'center';
export type RegionSort = 'label' | 'flow' | 'none';
export type ChildFlow = 'row' | 'column' | 'grid';
/** How the children inside a group are laid out. */
export type BlocksChildLayout = {
    /** 'row', 'column' or 'grid', as for a region. */
    flow: ChildFlow;
    /** For 'grid': exactly this many columns. Null lets the shape decide. */
    columns: number | null;
    /** For 'grid' when columns is null: exactly this many rows. */
    rows: number | null;
    align: RegionAlign;
    sortBy: RegionSort;
};
/** Lines each block up with the block in another region it is connected to by a reference. */
export type BlocksAlignWith = {
    /** The name of the region whose blocks these line up with, e.g. a row of value stream stages. */
    region: string;
    /** Reference type names that count, exactly as given in the context. Empty counts any reference. */
    referenceTypes: string[];
};
/** One named area of the template and the nodes that go in it. */
export type BlocksRegion = {
    /** The area name, exactly as written in `areas`. */
    name: string;
    /** Component type names, exactly as given in the context. Nodes of these types go here. */
    componentTypes: string[];
    /** Exact node labels, for a named group or a specific component. Nodes with these labels go here. */
    labels: string[];
    /** Labels of nodes whose whole subtree goes here: each named node and everything beneath it in the component hierarchy. */
    descendantsOf: string[];
    /** When true, every node no other region claimed goes here. Use on at most one region. */
    remaining: boolean;
    /**
     * 'row' lines the nodes up left to right, 'column' stacks them top to bottom, 'grid' wraps them
     * to fill the area. A row or column of more than 12 wraps onto further rows (or columns) in a
     * landscape block. 'tree' draws each hierarchy as a capability map: the top node spanning a row,
     * its children as columns, and their descendants stacked beneath them.
     */
    flow: RegionFlow;
    /** 'start' packs the nodes into the top left of the area, 'center' centers them in it. */
    align: RegionAlign;
    /**
     * 'label' orders the nodes alphabetically, 'flow' along the references between them (sources
     * first, e.g. value stream stages by 'Flows To'), 'none' keeps their current order.
     */
    sortBy: RegionSort;
    /** For 'grid' or 'row': exactly this many columns, e.g. 2, wrapping onto more rows. Null lets the shape decide. */
    columns: number | null;
    /** For 'column', or 'grid' when columns is null: exactly this many rows. */
    rows: number | null;
    /** In a 'column' flow each node spans the area's full width; in a 'row' flow, its full height. Ignored for 'grid' and 'tree'. */
    stretch: boolean;
    /**
     * How to lay out the children inside each group this region holds, e.g. two columns of
     * children per group. Null leaves them to the default layout.
     */
    childLayout: BlocksChildLayout | null;
    /**
     * Line each block of this region up with the block in another region it is connected to by a
     * reference. The template decides the direction: with this area below that region's, each block
     * goes in its related block's column, under it; above, over it; to the right or left, in its
     * row, beside it. Blocks connected to nothing there are placed after. The other region may itself
     * be aligned, for chains. Null when blocks are not placed by their references.
     */
    alignWith: BlocksAlignWith | null;
};
/** An arrangement of one group's children. */
export type BlocksArrangement = {
    /** The id of the group whose children to arrange, or null for the top level. */
    containerId: string | null;
    /**
     * CSS grid-template-areas, one string per row, area names separated by spaces.
     * Repeat a name to make its area span several columns or rows; every area must
     * be a rectangle. Use '.' for an empty cell. Example — a band across the top,
     * a column down the left and the rest beside it: ["top top", "left main"].
     * An empty list, with no regions, clears the group's arrangement.
     */
    areas: string[];
    /** One entry per area name used in `areas`. */
    regions: BlocksRegion[];
};
/** A region-based layout for the graph the user is viewing in Assistant Blocks. */
export type BlocksArrangementRecipe = {
    /** A short, descriptive title for the layout. */
    title: string;
    /** One arrangement per group you want to lay out. Usually just the top level (containerId null). */
    arrangements: BlocksArrangement[];
};
export const DEFAULT_TITLE = 'Layout';
const FLOWS: readonly RegionFlow[] = ['row', 'column', 'grid', 'tree'];
const CHILD_FLOWS: readonly ChildFlow[] = ['row', 'column', 'grid'];
const MAX_TRACK_COUNT = 50;
const ALIGNS: readonly RegionAlign[] = ['start', 'center'];
const SORTS: readonly RegionSort[] = ['label', 'flow', 'none'];
const oneOf = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T => allowed.find(option => option === value) ?? fallback;
/**
 * The Bedrock/LiteLLM tool-calling path can stringify array and object
 * arguments, so a JSON string is decoded before it is read.
 */
const decodeJson = (raw: unknown): unknown => {
    if (typeof raw !== 'string')
        return raw;
    try {
        return JSON.parse(raw);
    }
    catch {
        return raw;
    }
};
const asArray = (raw: unknown): unknown[] => {
    const decoded = decodeJson(raw);
    if (Array.isArray(decoded))
        return decoded;
    if (isRecord(decoded))
        return [decoded];
    return [];
};
const asStrings = (raw: unknown): string[] => asArray(raw).filter((value): value is string => typeof value === 'string' && value.length > 0);
const stripQuotes = (line: string): string => line.replace(/["']/g, '');
/** Tolerates one newline-separated string as well as one string per row. */
const parseAreas = (raw: unknown): string[] => {
    const decoded = decodeJson(raw);
    const lines = typeof decoded === 'string' ? decoded.split('\n') : asStrings(decoded);
    return lines
        .map(stripQuotes)
        .map(line => line.trim())
        .filter(line => line.length > 0);
};
const asTrackCount = (raw: unknown): number | null => {
    const n = typeof raw === 'string' ? Number(raw) : raw;
    return typeof n === 'number' && Number.isInteger(n) && n > 0
        ? Math.min(n, MAX_TRACK_COUNT)
        : null;
};
const parseChildLayout = (raw: unknown): BlocksChildLayout | null => {
    const value = decodeJson(raw);
    if (!isRecord(value))
        return null;
    return {
        flow: oneOf(value.flow, CHILD_FLOWS, 'grid'),
        columns: asTrackCount(value.columns),
        rows: asTrackCount(value.rows),
        align: oneOf(value.align, ALIGNS, 'start'),
        sortBy: oneOf(value.sortBy, SORTS, 'none'),
    };
};
const parseAlignWith = (raw: unknown): BlocksAlignWith | null => {
    const value = decodeJson(raw);
    if (!isRecord(value))
        return null;
    if (typeof value.region !== 'string' || value.region.trim().length === 0) {
        return null;
    }
    return {
        region: value.region.trim(),
        referenceTypes: asStrings(value.referenceTypes),
    };
};
const parseRegion = (value: unknown): BlocksRegion | null => {
    if (!isRecord(value))
        return null;
    if (typeof value.name !== 'string' || value.name.trim().length === 0) {
        return null;
    }
    return {
        name: value.name.trim(),
        componentTypes: asStrings(value.componentTypes),
        labels: asStrings(value.labels),
        descendantsOf: asStrings(value.descendantsOf),
        remaining: value.remaining === true,
        flow: oneOf(value.flow, FLOWS, 'grid'),
        align: oneOf(value.align, ALIGNS, 'start'),
        sortBy: oneOf(value.sortBy, SORTS, 'none'),
        columns: asTrackCount(value.columns),
        rows: asTrackCount(value.rows),
        stretch: value.stretch === true,
        childLayout: parseChildLayout(value.childLayout),
        alignWith: parseAlignWith(value.alignWith),
    };
};
const isRegion = (region: BlocksRegion | null): region is BlocksRegion => region !== null;
const parseArrangement = (value: unknown): BlocksArrangement | null => {
    if (!isRecord(value))
        return null;
    const areas = parseAreas(value.areas);
    const regions = asArray(value.regions).map(parseRegion).filter(isRegion);
    const rawRegions = decodeJson(value.regions);
    const isCleared = areas.length === 0 && Array.isArray(rawRegions) && rawRegions.length === 0;
    if (areas.length === 0 && !isCleared)
        return null;
    return {
        containerId: typeof value.containerId === 'string' && value.containerId.length > 0
            ? value.containerId
            : null,
        areas,
        regions,
    };
};
/** An arrangement that lays nothing out, returning the group to its default layout. */
export const clearedArrangement = (containerId: string | null): BlocksArrangement => ({ containerId, areas: [], regions: [] });
const isArrangement = (arrangement: BlocksArrangement | null): arrangement is BlocksArrangement => arrangement !== null;
/** Arrangements from any untrusted source: model output or stored view settings. */
export const parseArrangements = (raw: unknown): BlocksArrangement[] => asArray(raw).map(parseArrangement).filter(isArrangement);
/** A stored layout title, or null when there is none worth showing. */
export const parseTitle = (raw: unknown): string | null => typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
const hasChildLayout = (arrangement: BlocksArrangement): boolean => arrangement.regions.some(region => region.childLayout !== null);
const childLayoutsFirst = (arrangements: BlocksArrangement[]): BlocksArrangement[] => {
    const isTopLevel = (arrangement: BlocksArrangement): boolean => arrangement.containerId === null;
    const withChildLayout = arrangements.filter(hasChildLayout);
    return [
        ...withChildLayout.filter(isTopLevel),
        ...withChildLayout.filter(arrangement => !isTopLevel(arrangement)),
        ...arrangements.filter(arrangement => !hasChildLayout(arrangement)),
    ];
};
/** A group arrangement with one region holding every child: what a `childLayout` expresses. */
const isWholeGroupLayout = ({ containerId, regions, }: BlocksArrangement): boolean => containerId !== null &&
    regions.length === 1 &&
    regions[0].remaining &&
    regions[0].componentTypes.length === 0 &&
    regions[0].labels.length === 0 &&
    regions[0].descendantsOf.length === 0;
const isSameArrangement = (a: BlocksArrangement, b: BlocksArrangement): boolean => JSON.stringify(a) === JSON.stringify(b);
const byName = (a: BlocksRegion, b: BlocksRegion): number => {
    if (a.name === b.name)
        return 0;
    return a.name < b.name ? -1 : 1;
};
const childLayoutsOf = ({ regions }: BlocksArrangement): string => JSON.stringify(regions
    .filter(({ childLayout }) => childLayout !== null)
    .toSorted(byName)
    .map(({ name, childLayout }) => [name, childLayout]));
const isTopLevelWithSameChildLayouts = (stored: BlocksArrangement, replacement: BlocksArrangement): boolean => replacement.containerId === null &&
    hasChildLayout(replacement) &&
    childLayoutsOf(stored) === childLayoutsOf(replacement);
/**
 * Each incoming arrangement replaces any earlier one for the same group, and a
 * recipe naming a group twice keeps the last. The result is ordered oldest
 * first, and within a recipe an arrangement with a `childLayout` comes before
 * one that names a group it holds, the top level's first, so the named group's
 * own arrangement wins.
 *
 * A recipe with a `childLayout` that repeats a stored whole-group layout
 * unchanged leaves it in its older place, where the `childLayout` can override it.
 * A resent top level whose `childLayout`s are unchanged keeps its stored place,
 * so it overrides no group arrangement it did not already override.
 */
export const mergeArrangements = (current: BlocksArrangement[], incoming: BlocksArrangement[]): BlocksArrangement[] => {
    const isEcho = (arrangement: BlocksArrangement): boolean => isWholeGroupLayout(arrangement) &&
        current.some(stored => isSameArrangement(stored, arrangement));
    const changed = incoming.some(hasChildLayout)
        ? incoming.filter(arrangement => !isEcho(arrangement))
        : incoming;
    const incomingByContainerId = new Map(childLayoutsFirst(changed).map(arrangement => [
        arrangement.containerId,
        arrangement,
    ]));
    const inPlace = new Set(current.flatMap(stored => {
        const replacement = incomingByContainerId.get(stored.containerId);
        return replacement && isTopLevelWithSameChildLayouts(stored, replacement)
            ? [replacement]
            : [];
    }));
    return [
        ...current.flatMap(stored => {
            const replacement = incomingByContainerId.get(stored.containerId);
            if (!replacement)
                return [stored];
            return inPlace.has(replacement) ? [replacement] : [];
        }),
        ...[...incomingByContainerId.values()].filter(arrangement => !inPlace.has(arrangement)),
    ];
};
/**
 * Normalizes raw LLM output, dropping anything unusable. Returns `null` only
 * when the input is not an object at all.
 */
export const parseArrangementRecipe = (json: unknown): BlocksArrangementRecipe | null => {
    if (!isRecord(json))
        return null;
    return {
        title: parseTitle(json.title) ?? DEFAULT_TITLE,
        arrangements: parseArrangements(json.arrangements),
    };
};
